import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Music4, PartyPopper, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const IMG =
  "https://images.unsplash.com/photo-1714893656818-9bd2b65c11ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtdXNpYyUyMHJlY29yZGluZyUyMHN0dWRpbyUyMG1pY3JvcGhvbmUlMjBoZWFkcGhvbmVzJTIwdmlueWwlMjBhbGJ1bSUyMHNvdW5kJTIwbWl4aW5nJTIwY29uc29sZXxlbnwwfHx8fDE3OTE1MTk0ODh8MA&ixlib=rb-4.1.0&q=85";

const FLOW = [
  "Tell us the occasion and the person — a few bullet points are enough.",
  "We write the lyric and send it to you for confirmation.",
  "You confirm and pay by card or Swiss QR bank transfer.",
  "Production starts immediately; private packages arrive within 24 hours.",
];

export const PrivateHero = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="fade-up">
            <p className="eyebrow">{t("Private & Events")}</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t("Songs for the moments that matter")}
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
              {t("Birthdays, weddings, anniversaries — or simply because someone deserves it. A tailor-made song is the gift nobody expects and nobody forgets.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/order?package=private" data-testid="private-hero-order-cta" className="btn-primary">
                {t("Start Your Order")} <ArrowRight size={16} />
              </Link>
              <Link to="/order?package=event" data-testid="private-hero-event-cta" className="btn-ghost">
                <PartyPopper size={16} /> {t("Event Song from CHF 150")}
              </Link>
            </div>
          </div>
          <div className="studio-card fade-up overflow-hidden" style={{ animationDelay: "0.15s" }}>
            <img src={IMG} alt={t("Studio headphones and condenser microphone ready for a vocal recording")} className="h-72 w-full object-cover sm:h-96" />
          </div>
        </div>
      </section>
  );
};

export const PrivateOffers = () => {
  const { t } = useLanguage();
  return (
      <section className="border-y border-[#282C3D] bg-[#0E1018]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="eyebrow">{t("Offers")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold text-slate-100 sm:text-3xl">{t("Two simple ways to gift music")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div data-testid="private-package-card" className="studio-card fade-up border-[#E5A93C]/40 p-8">
              <div className="flex items-center justify-between">
                <Music4 className="text-[#E5A93C]" size={24} />
                <span className="rounded-full bg-[#E5A93C]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#FFC857]">
                  {t("Best Value for Personal Gifts")}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-slate-100">{t("Private Custom Song Package")}</h3>
              <p className="mt-2 font-display text-3xl font-bold text-[#FFC857]">CHF 300</p>
              <p className="mt-1 flex items-center gap-2 font-mono text-xs text-slate-500">
                <Clock size={13} className="shrink-0" /> {t("Within 24 hours after lyric confirmation & payment receipt")}
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {[
                  "6 individual songs in 3 different music styles (2 per style)",
                  "Fully customized lyrics based on your story",
                  "Professional studio mixing & mastering",
                  "High-resolution WAV & MP3 downloads",
                  "Personal, non-commercial usage rights",
                ].map((f) => (
                  <li key={f} className="flex gap-2.5">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#10B981]" />
                    {t(f)}
                  </li>
                ))}
              </ul>
              <Link to="/order?package=private" data-testid="private-package-cta" className="btn-primary mt-8 w-full">
                {t("Order the Package")} <ArrowRight size={16} />
              </Link>
            </div>

            <div data-testid="event-package-card" className="studio-card fade-up p-8" style={{ animationDelay: "0.1s" }}>
              <div className="flex items-center justify-between">
                <PartyPopper className="text-[#E5A93C]" size={24} />
                <span className="rounded-full border border-[#282C3D] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  {t("Birthdays & Weddings")}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-slate-100">{t("Private Event Song")}</h3>
              <p className="mt-2 font-display text-3xl font-bold text-[#FFC857]">{t("from CHF 150")}</p>
              <p className="mt-1 font-mono text-xs text-slate-500">{t("Guided express turnaround")}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {[
                  "2 distinct versions of the same personalized song",
                  "Occasion-tailored melody & lyrics",
                  "Choice of male or female studio vocalist",
                  "Slideshow-ready audio format for your event",
                ].map((f) => (
                  <li key={f} className="flex gap-2.5">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#10B981]" />
                    {t(f)}
                  </li>
                ))}
              </ul>
              <Link to="/order?package=event" data-testid="event-package-cta" className="btn-ghost mt-8 w-full">
                {t("Plan the Event Song")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
  );
};

export const PrivateProcess = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <p className="eyebrow">{t("Guided Flow")}</p>
        <h2 className="mt-3 font-display text-2xl font-bold text-slate-100 sm:text-3xl">{t("Effortless from first note to final master")}</h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {FLOW.map((f, i) => (
            <div key={f} data-testid={`private-flow-step-${i + 1}`} className="studio-card fade-up flex items-start gap-4 p-6" style={{ animationDelay: `${i * 0.08}s` }}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#E5A93C]/50 font-mono text-sm text-[#FFC857]">
                {i + 1}
              </span>
              <p className="pt-1.5 text-sm leading-relaxed text-slate-300">{t(f)}</p>
            </div>
          ))}
        </div>
      </section>
  );
};

export default function PrivateEvents() {
  return <div><PrivateHero /><PrivateOffers /><PrivateProcess /></div>;
}
